import { useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type TableHeaderRow } from "@devexpress/dx-react-grid-bootstrap4";
import { DEFAULT_SORTING } from "@features/landing-page-analytics/context";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import { type ColumnDef } from "@features/landing-page-analytics/types";
import { CreativeMetricType } from "@north-beam/nb-client-side";

import { NAME_COLUMN_ID, URL_COLUMN_ID } from "../constants";

import { ColumnMenu } from "./column-menu";
import { NumericFilterPopover } from "./numeric-filter-popover";

const LEFT_ALIGNED: string[] = [URL_COLUMN_ID, NAME_COLUMN_ID];

export const TableHeaderContent = ({
  column,
  children,
}: TableHeaderRow.ContentProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const columnDef = column as ColumnDef;
  const {
    tableState: { sorting, metricFilters },
    setSorting,
    setMetricFilters,
    setPage,
  } = useLandingPageAnalyticsContext();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const metricFilter = useMemo(() => {
    return metricFilters.find((e) => e.id === columnDef.name);
  }, [columnDef.name, metricFilters]);

  return (
    <>
      <div
        className={clsx("text-xs w-full flex items-center px-2", {
          "justify-end": !LEFT_ALIGNED.includes(columnDef.name),
        })}
      >
        <span
          className={clsx("overflow-hidden min-w-[10px] flex items-center", {
            "flex-1": LEFT_ALIGNED.includes(columnDef.name),
          })}
        >
          {columnDef.metric?.composition?.type ===
            CreativeMetricType.user_defined && (
            <i className="fa-solid fa-sharp fa-function text-xxs text-cosmic mr-1" />
          )}
          {children}
        </span>
        <ColumnMenu
          column={columnDef}
          onSelect={(val) => {
            if (val === "asc" || val === "desc") {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "onChange",
                additionalActionInfo: "Change Table Sorting",
              });

              if (sorting.id === columnDef.name && sorting.order === val) {
                setSorting({ ...DEFAULT_SORTING });
              } else {
                setSorting({ id: columnDef.name, order: val });
              }
            }

            if (val === "filter") {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "onChange",
                additionalActionInfo: "Change Table Filtering",
              });

              setIsFilterOpen(true);
            }
          }}
        />
        {(isFilterOpen || metricFilter) && (
          <NumericFilterPopover
            isOpen={isFilterOpen}
            onOpen={() => setIsFilterOpen(true)}
            onClose={() => {
              setIsFilterOpen(false);
            }}
            onReset={() => {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "Other",
                additionalActionInfo: "Reset Table Filtering",
              });

              setPage(1);
              setMetricFilters(
                metricFilters.filter((e) => e.id !== columnDef.name),
              );
            }}
            operator={metricFilter?.operator}
            value={metricFilter?.value}
            onApply={(value, operator) => {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "Other",
                additionalActionInfo: "Apply Table Filtering",
              });

              setPage(1);
              setMetricFilters([
                ...metricFilters.filter((e) => e.id !== columnDef.name),
                { id: columnDef.name, operator, value },
              ]);
            }}
          />
        )}
        {sorting.id === columnDef.name && (
          <div
            className={clsx(
              "flex justify-center items-center bg-cosmic-3",
              "rounded w-6 h-6 ml-0.5 cursor-pointer text-cosmic",
            )}
            onClick={() => {
              setSorting({
                id: columnDef.name,
                order: sorting.order === "desc" ? "asc" : "desc",
              });
            }}
          >
            <motion.i
              initial={false}
              animate={{ rotate: sorting.order === "desc" ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="fa-regular fa-sharp fa-arrow-down"
            />
          </div>
        )}
      </div>
      <div className="header-column-separator"></div>
    </>
  );
};
